import { log } from 'src/services/app/log.service'

import {
  COUNTRIES_CODE_MAP,
  COUNTRY_REGIONS_MAP,
  COUNTRIES_WITH_REGIONS_CODE_MAP,
} from 'src/services/consts/countries.const'

// ============================================================================
// Date & Time Utilities
// ============================================================================

export function convertDateToUtcTS(date) {
  return Date.UTC(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds(),
  )
}

export function formatDateVerbal(dateStr) {
  const date = new Date(dateStr)
  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ]

  const day = date.getDate()
  const monthIndex = date.getMonth()
  const year = date.getFullYear()

  return `${monthNames[monthIndex]} ${formatDay(day)}, ${year}`
}

export const getRelativeTime = (timestamp, currentTime) => {
  if (!timestamp) return ''

  // Force reactivity by referencing currentTime
  currentTime.value

  const now = new Date()
  const diffMs = now - new Date(+timestamp)
  const diffMinutes = Math.floor(diffMs / (1000 * 60))

  if (diffMinutes < 3) {
    return 'just now'
  } else if (diffMinutes < 15) {
    return 'a few minutes ago'
  } else if (diffMinutes < 60) {
    return 'recently'
  } else {
    return 'a while ago'
  }
}

function formatDay(day) {
  if (day > 3 && day < 21) return day + 'th' // for 4th to 20th
  switch (day % 10) {
    case 1:
      return day + 'st'
    case 2:
      return day + 'nd'
    case 3:
      return day + 'rd'
    default:
      return day + 'th'
  }
}

// ============================================================================
// Validation Utilities
// ============================================================================

export function isValueEmpty(value) {
  return value === null || value === undefined || value === ''
}

export function isObjectEmpty(obj) {
  return Object.keys(obj).length === 0
}

export function isObjectEmptyDeep(obj) {
  if (!obj || typeof obj !== 'object') return true

  return !Object.values(obj).some((value) => {
    if (Array.isArray(value)) {
      return value.length > 0 && value.some((item) => !isValueEmpty(item?.toString().trim()))
    }
    return !isValueEmpty(value?.toString().trim())
  })
}

export function normalizeFormValue(value, fieldType) {
  if (fieldType === 'select') {
    return typeof value === 'string' ? value : (value?.value ?? null)
  }
  return value?.toString().trim() || ''
}

/** Basic format check (local@domain.tld); not a full RFC parser. */
export function isValidEmail(value) {
  const trimmed = String(value ?? '').trim()
  if (!trimmed) return false
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)
}

/** Form string/number → integer days, or `undefined` if empty/invalid (omit from API payload). */
export function parseOptionalDaysValidFor(value) {
  const raw = String(value ?? '').trim()
  if (!raw) return undefined
  const n = Number.parseInt(raw, 10)
  return Number.isNaN(n) ? undefined : n
}

export function validateUrl(url) {
  // Allow empty values (will be handled by required rule if needed)
  if (!url || url.trim() === '') return true

  // Add http:// if no protocol specified
  const urlToCheck = url.match(/^https?:\/\//) ? url : `http://${url}`

  try {
    const urlObj = new URL(urlToCheck)
    // Check if hostname has at least one dot AND something after the dot
    // This prevents domains like "google." from being valid
    const hostnameParts = urlObj.hostname.split('.')
    const isValidHostname =
      hostnameParts.length >= 2 &&
      hostnameParts[hostnameParts.length - 1].length > 0 &&
      hostnameParts.every((part) => part.length > 0)

    return isValidHostname
  } catch (e) {
    log.error('Error validating URL', e)
    return false
  }
}

const DANGEROUS_URL_SCHEMES = ['javascript:', 'data:', 'file:', 'vbscript:', 'about:', 'blob:']

/**
 * Quasar-style q-input rule: empty is valid, dangerous schemes blocked,
 * structurally validated via `validateUrl`. Returns `true` or an error string.
 */
export function validateOptionalUrl(value) {
  const trimmed = String(value ?? '').trim()
  if (!trimmed) return true
  const lower = trimmed.toLowerCase()
  if (DANGEROUS_URL_SCHEMES.some((scheme) => lower.startsWith(scheme))) {
    return 'URL scheme not allowed'
  }
  return validateUrl(trimmed) || 'Please enter a valid URL'
}

// ============================================================================
// String & URL Utilities
// ============================================================================

export function randomId() {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15)
}

export function convertHtmlToText(htmlString) {
  return htmlString
    .replace(/<br\s*\/?>/gi, '\n') // Replace <br> tags with newlines
    .replace(/<[^>]*>/g, '') // Remove any remaining HTML tags
    .trim()
}

// ============================================================================
// Country & Region Utilities
// ============================================================================

export function getRegionAbbreviation(regionName, countryCode) {
  const regions = COUNTRY_REGIONS_MAP[countryCode]
  if (!regions) return regionName

  // Case-insensitive lookup
  const region = regions.find((r) => r.regionName.toLowerCase() === regionName.toLowerCase())
  return region ? region.regionCode : regionName
}

export function getCountryNameByCode(countryCode) {
  if (!countryCode) return null

  // Find country by its 2-letter alpha2Code
  const country = Object.values(COUNTRIES_CODE_MAP).find(
    (country) => country.alpha2Code === countryCode,
  )
  return country?.countryName
}

export function getCountryOptions() {
  return Object.entries(COUNTRIES_CODE_MAP).map(([, value]) => ({
    label: value.countryName,
    value: value.alpha2Code,
  }))
}

export function getStateOptions(countryCode) {
  if (!countryCode) return []

  // Map 2-letter codes to 3-letter codes for COUNTRY_REGIONS_MAP
  const mappedCountryCode = COUNTRIES_WITH_REGIONS_CODE_MAP[countryCode]

  if (!mappedCountryCode || !COUNTRY_REGIONS_MAP[mappedCountryCode]) {
    return []
  }

  const regions = COUNTRY_REGIONS_MAP[mappedCountryCode]

  return regions.map((region) => ({
    label: region.regionName,
    value: region.regionCode,
  }))
}

// ============================================================================
// Search & Matching Utilities
// ============================================================================

/** Split on punctuation/whitespace; keep letters and numbers from any script. */
const SEARCH_TOKEN_SPLIT_REGEX = /[^\p{L}\p{N}]+/u
/** Strip separators from names for substring matching (same character set as tokens). */
const SEARCH_STRIP_SEPARATORS_REGEX = /[^\p{L}\p{N}]/gu

export function smartMatch(companies, searchTerm) {
  if (!searchTerm || !searchTerm.trim()) {
    return companies
  }

  const searchLower = searchTerm.toLowerCase().trim()

  const searchWords = searchLower.split(SEARCH_TOKEN_SPLIT_REGEX).filter((word) => word.length > 0)

  // Pure punctuation/whitespace yields no tokens — return no matches
  if (searchWords.length === 0) return []

  // Helper to check if search term matches company name acronym
  const matchesAcronym = (companyName) => {
    if (!companyName) return false
    const words = companyName.trim().split(/\s+/)
    if (words.length < 2) return false // Need at least 2 words for acronym
    const acronym = words
      .map((word) => word.charAt(0))
      .join('')
      .toLowerCase()
    return acronym === searchLower || acronym.startsWith(searchLower)
  }

  // Filter and score companies that match the search term
  const scoredCompanies = companies
    .filter((company) => {
      if (!company) return false

      const name = company.name || ''
      const legalName = company.legalName || ''

      // Check acronym match
      if (matchesAcronym(name) || matchesAcronym(legalName)) {
        return true
      }

      const cleanName = name.toLowerCase().replace(SEARCH_STRIP_SEPARATORS_REGEX, '')
      const cleanLegalName = legalName.toLowerCase().replace(SEARCH_STRIP_SEPARATORS_REGEX, '')

      // Check if all search words are contained in either name (in any order)
      return searchWords.every((word) => cleanName.includes(word) || cleanLegalName.includes(word))
    })
    .map((company) => {
      const name = (company.name || '').toLowerCase()
      const legalName = (company.legalName || '').toLowerCase()

      let score = 0

      // Exact match - highest priority
      if (name === searchLower || legalName === searchLower) {
        score = 100
      }
      // Starts with search term - high priority
      else if (name.startsWith(searchLower) || legalName.startsWith(searchLower)) {
        score = 80
      }
      // Acronym match - very high priority
      else if (matchesAcronym(company.name) || matchesAcronym(company.legalName)) {
        score = 50
      }

      // Word boundary match - medium priority
      else if (
        name.split(/\s+/).some((word) => word.startsWith(searchLower)) ||
        legalName.split(/\s+/).some((word) => word.startsWith(searchLower))
      ) {
        score = 25
      }
      // Contains match - lower priority
      else {
        score = 10
      }

      return { company, score }
    })
    .sort((a, b) => {
      // Sort by score (descending), then alphabetically by name
      if (b.score !== a.score) {
        return b.score - a.score
      }
      return (a.company.name || '').localeCompare(b.company.name || '')
    })
    .map(({ company }) => company)

  return scoredCompanies
}

export function smartCountryMatch(countries, searchTerm) {
  if (!searchTerm || !searchTerm.trim()) {
    return countries
  }

  const searchLower = searchTerm.trim().toLowerCase()

  return countries
    .filter((country) => {
      const label = country.label.toLowerCase()
      const twoLetter = (country.twoLetterCode || '').toLowerCase()
      const threeLetter = (country.threeLetterCode || '').toLowerCase()

      // Match against country name, 2-letter code, or 3-letter code
      return (
        label.includes(searchLower) ||
        twoLetter.includes(searchLower) ||
        threeLetter.includes(searchLower)
      )
    })
    .map((country) => {
      const label = country.label.toLowerCase()
      const twoLetter = (country.twoLetterCode || '').toLowerCase()
      const threeLetter = (country.threeLetterCode || '').toLowerCase()

      let score = 0

      // Exact match on codes - highest priority
      if (twoLetter === searchLower || threeLetter === searchLower) {
        score = 100
      }
      // Exact match on name
      else if (label === searchLower) {
        score = 90
      }
      // Starts with search term (name)
      else if (label.startsWith(searchLower)) {
        score = 50
      }
      // Word boundary match (any word in name starts with search)
      else if (label.split(/\s+/).some((word) => word.startsWith(searchLower))) {
        score = 25
      }
      // Contains match
      else {
        score = 10
      }

      return { ...country, score }
    })
    .sort((a, b) => {
      // Sort by score (descending), then alphabetically by label
      if (b.score !== a.score) {
        return b.score - a.score
      }
      return a.label.localeCompare(b.label)
    })
}

// ============================================================================
// Clipboard Utilities
// ============================================================================

export async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text)
    return { success: true }
  } catch (error) {
    console.error('Clipboard API failed:', error)

    // Fallback for older browsers or if clipboard API fails
    try {
      const textArea = document.createElement('textarea')
      textArea.value = text
      textArea.style.position = 'fixed'
      textArea.style.left = '-999999px'
      textArea.style.top = '-999999px'
      document.body.appendChild(textArea)
      textArea.focus()
      textArea.select()
      document.execCommand('copy')
      document.body.removeChild(textArea)
      return { success: true }
    } catch (fallbackError) {
      console.error('Fallback copy failed:', fallbackError)
      return { success: false, error: fallbackError }
    }
  }
}
